import { useState, useEffect, useCallback } from 'react';
import { X, MessageSquare, Loader2 } from 'lucide-react';
import { paymentReconApi } from '../../services/paymentReconApi';
import { useToast } from '../ui/Toast';
import { InvoiceComments } from './InvoiceComments';
import type { PaymentReconComment } from '../../types/paymentRecon';

export interface CommentTarget {
    invoiceId: number;
    invoiceNo: string;
    customerName: string | null;
}

interface Props {
    target: CommentTarget;
    canEdit: boolean;
    onClose: () => void;
    /** Fired after a comment is added/edited/deleted so list badges can refresh. */
    onChanged: () => void;
}

/**
 * Right-hand side panel showing the comment thread for one invoice. Opened from
 * any list on the page (Invoices, Payments/Reconcile, MatchPanel). Rendered at
 * z-50 so it stacks above the MatchPanel drawer (z-40).
 */
export function InvoiceCommentsPanel({ target, canEdit, onClose, onChanged }: Props) {
    const { toast } = useToast();
    const [comments, setComments] = useState<PaymentReconComment[]>([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await paymentReconApi.listInvoiceComments(target.invoiceId);
            setComments(res);
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed to load comments', 'error');
        } finally {
            setLoading(false);
        }
    }, [target.invoiceId, toast]);

    useEffect(() => { void load(); }, [load]);

    // Esc closes this panel (it owns the topmost overlay).
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const handleChanged = () => {
        void load();
        onChanged();
    };

    return (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
            <div
                className="flex h-full w-full max-w-md flex-col bg-white shadow-xl"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-start justify-between border-b border-slate-200 p-4">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 text-base font-semibold text-slate-800">
                            <MessageSquare className="h-4 w-4 text-amber-600" />
                            {target.invoiceNo}
                        </div>
                        {target.customerName && (
                            <div className="mt-0.5 truncate text-sm text-slate-500" title={target.customerName}>{target.customerName}</div>
                        )}
                    </div>
                    <button onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
                </div>

                {/* Comments */}
                <div className="flex-1 overflow-y-auto p-4">
                    {loading ? (
                        <div className="flex justify-center py-10 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
                    ) : (
                        <InvoiceComments
                            invoiceId={target.invoiceId}
                            comments={comments}
                            canEdit={canEdit}
                            onChanged={handleChanged}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
